import axios from "axios";
import { removeNullProperty } from "@/utils/object";

const BASE_URL = "/v1";
const OIDC_CLIENT_ID = "abr";

export const getAccessToken = () => {
  try {
    const key = Object.keys(localStorage).find(
      (key) =>
        key.startsWith("oidc.user:") && key.endsWith(`:${OIDC_CLIENT_ID}`),
    );

    if (!key) return null;

    const rawUser = localStorage.getItem(key);
    if (!rawUser) return null;

    const user = JSON.parse(rawUser);

    return user?.access_token || null;
  } catch (error) {
    console.error("OIDC token read failed", error);
    return null;
  }
};

const roleHeader = async (role) => {
  const store = (await import("@/store")).default;

  const userRole = store.state?.role ? store.state?.role : role;
  // console.log('aut :: role :>> ', role);
  // console.log('aut :: store.state?.role :>> ', store.state?.role);
  // console.log('aut :: userRole :>> ', userRole);

  const header = userRole && userRole > 1 ? { "x-abr-postid": userRole } : {}; //TODO This shouldn't be done here. x-abr-postid header needs to be sent with all requests

  // console.log('aut :: header :>> ', header);
  return header;
};

export const sendRequest = async ({
  auth = "",
  url,
  params = {},
  method = "GET",
  body = "",
  headers = {},
}) => {
  const store = (await import("@/store")).default;

  const accessToken = getAccessToken();

  const token =
    auth !== "" ? auth : accessToken ? `Bearer ${accessToken}` : null;

  const locale =
    store.state.locale ??
    (localStorage.getItem("lang") === "en" ? "en-US" : "fa-IR");

  const options = {
    url: `${BASE_URL}${url}`,
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
      "Accept-Language": locale,
      ...headers,
    },
  };

  if (method !== "GET") {
    options.data = body;
  }

  if (params && Object.keys(params).length > 0) {
    options.params = params;
  }

  try {
    const response = await axios(options);

    if (response?.status >= 200 && response?.status < 300) {
      const { data } = response;
      return removeNullProperty(data);
    }

    throw new Error("Error receiving information");
  } catch (err) {
    console.error(`Request to ${url} failed`, err);
    throw err;
  }
};

let _instance;
export const instance = () => {
  if (_instance)
    return {
      ..._instance,
      baseUrl: `${window.location.protocol}//${window.location.host}${process.env.BASE_URL}`,
    };
  else
    throw new Error(
      "Instance not available. Please call fetchInstance() first",
    );
};

export const fetchInstance = async () => {
  _instance = Object.assign(
    await sendRequest({
      url: "/$instance",
    }),
    await sendRequest({
      url: "/def/$instance",
    }),
  );
  return instance();
};

// Decode server errors received as JSON, text, Blob or ArrayBuffer.
const decodeServerData = async (data) => {
  if (data instanceof ArrayBuffer) {
    data = new TextDecoder("utf-8").decode(data);
  } else if (ArrayBuffer.isView(data)) {
    data = new TextDecoder("utf-8").decode(data);
  } else if (typeof Blob !== "undefined" && data instanceof Blob) {
    data = await data.text();
  }

  if (typeof data !== "string") return data;
  const trimmed = data.trim();
  if (/^<!doctype\s+html|^<html\b/i.test(trimmed)) {
    const doc = new DOMParser().parseFromString(trimmed, "text/html");
    const message = Array.from(doc.body.querySelectorAll("h1,h2,h3,p,li,pre"))
      .map((el) => el.textContent.trim())
      .filter(Boolean)
      .join("\n\n");
    return {
      errorCode: "HTML_ERROR",
      label: { args: { msg: message || doc.body.textContent.trim() } },
    };
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return data;
  }
};

const prepareDialogContent = (data) => {
  const payload = data?.$exception || data;
  // Keep existing translated $messages and $exception structures intact.
  if (data?.$messages || data?.$exception) return data;
  if (payload?.label?.args?.msg) {
    return { $exception: { ...payload, label: payload.label } };
  }
  if (typeof payload === "string") return payload;
  if (payload?.message || payload?.title)
    return String(payload.message || payload.title);
  return "خطایی در دریافت اطلاعات رخ داده است.";
};

let oidcManager = null;
let refreshPromise = null;
let logoutPromise = null;

const isPermanentRefreshError = (error) => {
  const codes = [
    error?.error,
    error?.response?.data?.error,
    error?.code,
    error?.message,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase().trim());

  const permanentErrors = [
    "invalid_grant",
    "invalid_token",
    "login_required",
    "interaction_required",
  ];

  return codes.some((code) =>
    permanentErrors.some(
      (permanent) =>
        code === permanent ||
        code.startsWith(`${permanent}:`) ||
        code.startsWith(`${permanent} `),
    ),
  );
};

export const setOidcManager = (manager) => {
  oidcManager = manager;
};

const refreshAccessToken = () => {
  if (logoutPromise) {
    return Promise.reject(new Error("Session is ending"));
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  if (!oidcManager) {
    return Promise.reject(new Error("OIDC manager is not initialized"));
  }

  const pending = (async () => {
    const currentUser = await oidcManager.getUser();

    if (!currentUser?.refresh_token) {
      const error = new Error("Refresh token unavailable");
      error.code = "REFRESH_TOKEN_UNAVAILABLE";
      throw error;
    }

    const user = await oidcManager.signinSilent();

    if (!user?.access_token || user.expired) {
      throw new Error("Invalid refreshed access token");
    }

    return user.access_token;
  })();

  refreshPromise = pending;

  void pending.then(
    () => {
      if (refreshPromise === pending) {
        refreshPromise = null;
      }
    },
    () => {
      if (refreshPromise === pending) {
        refreshPromise = null;
      }
    },
  );

  return pending;
};

const logoutUser = async (store) => {
  if (!logoutPromise) {
    logoutPromise = Promise.resolve().then(async () => {
      try {
        await store.state.auth?.signOut?.();
      } catch (error) {
        console.error("Logout failed", error);
        await oidcManager?.removeUser();
        window.location.replace("/");
      }
    });
  }

  return logoutPromise;
};

axios.interceptors.request.use(
  async (config) => {
    const store = (await import("@/store")).default;

    if (logoutPromise) {
      throw new Error("Session is ending");
    }

    if (refreshPromise && !config._retry) {
      await refreshPromise;
    }

    if (logoutPromise) {
      throw new Error("Session is ending");
    }

    if (!config.url?.includes("$lookup")) {
      store.commit("SET_LOADING", true);
    }

    config.headers = config.headers || {};

    const token = getAccessToken();

    if (token && !config._retry) {
      if (
        !config.headers.Authorization ||
        String(config.headers.Authorization).startsWith("Bearer ")
      ) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axios.interceptors.response.use(
  async (response) => {
    const store = (await import("@/store")).default;

    store.commit("SET_LOADING", false);

    if (["arraybuffer", "blob"].includes(response.config?.responseType)) {
      const type = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase();

      const isErrorContent =
        type.includes("application/json") || type.includes("text/html");

      if (isErrorContent) {
        const parsed = await decodeServerData(response.data);

        if (
          typeof parsed === "string" ||
          parsed?.errorCode ||
          parsed?.$exception ||
          parsed?.label
        ) {
          const error = new Error("File request returned an error payload");

          error.response = {
            ...response,
            data: parsed,
          };

          error.config = response.config;
          error.handledByInterceptor = true;

          void store.dispatch("SHOW_MESSAGE", {
            title: "خطا در دریافت اطلاعات",
            content: prepareDialogContent(parsed),
            headers: response.headers || {},
          });

          return Promise.reject(error);
        }
      }
    }



    if (response.data?.$messages?.length > 0) {
      await store.dispatch("SHOW_MESSAGE", {
        title: "پیغام سیستمی",
        content: response.data,
        headers: response.headers,
      });
    }

    if (
      response.data instanceof ArrayBuffer ||
      (typeof Blob !== "undefined" && response.data instanceof Blob)
    ) {
      return response;
    }

    response.data = await removeNullProperty(response.data);

    return response;
  },

  async (error) => {
    const store = (await import("@/store")).default;

    store.commit("SET_LOADING", false);

    if (!error?.response) {
      return Promise.reject(error);
    }

    const response = error.response;
    const originalRequest = error.config;

    if (response.status === 401 && originalRequest) {
      if (logoutPromise || originalRequest._retry) {
        return Promise.reject(error);
      }

      const failedAuthorization =
        originalRequest.headers?.Authorization ||
        originalRequest.headers?.authorization;

      if (
        !failedAuthorization ||
        !String(failedAuthorization).startsWith("Bearer ")
      ) {
        return Promise.reject(error);
      }

      const requestUrl = new URL(
        originalRequest.url,
        originalRequest.baseURL || window.location.origin,
      );

      if (
        requestUrl.origin !== window.location.origin ||
        !requestUrl.pathname.startsWith("/v1/")
      ) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      let newToken;

      try {
        if (refreshPromise) {
          newToken = await refreshPromise;
        } else {
          const currentToken = getAccessToken();

          if (
            currentToken &&
            failedAuthorization !== `Bearer ${currentToken}`
          ) {
            newToken = currentToken;
          } else {
            newToken = await refreshAccessToken();
          }
        }
      } catch (refreshError) {
        if (
          isPermanentRefreshError(refreshError) ||
          refreshError?.code === "REFRESH_TOKEN_UNAVAILABLE"
        ) {
          try {
            await logoutUser(store);
          } catch (logoutError) {
            console.error("Logout failed", logoutError);
          }
        }

        return Promise.reject(refreshError);
      }

      if (logoutPromise) {
        return Promise.reject(error);
      }

      originalRequest.headers = originalRequest.headers || {};

      delete originalRequest.headers.authorization;

      originalRequest.headers.Authorization = `Bearer ${newToken}`;

      return axios(originalRequest);
    }

    const data = await decodeServerData(response.data);

    response.data = data;

    const exception = data?.$exception || data;
    const errorCode = exception?.errorCode;

    let content = prepareDialogContent(data);

    if (["A2010001", "A2011003", "A2011004"].includes(errorCode)) {
      content =
        errorCode === "A2011004"
          ? "برای شخص بیش از یک کد بازاریاب یافت شد، لطفا با پشتیبانی تماس بگیرید"
          : "خطایی در پروفایل کاربر وجود دارد، با پشتیبانی تماس بگیرید";

      window.setTimeout(() => {
        store.state.auth?.signOut?.();
      }, 3000);
    }

    error.handledByInterceptor = true;

    void store.dispatch("SHOW_MESSAGE", {
      title: "خطا در دریافت اطلاعات",
      content,
      headers: response.headers || {},
    });

    return Promise.reject(error);
  },
);

export const submitDefData = async (payload, ver /*: string*/) => {
  const header = await roleHeader(undefined);
  const index = await sendRequest({
    url: "/def/$index",
    method: "GET",
    headers: {
      ...header,
    },
  });

  let current = index.pop();

  if (!current.match(`^${ver}/(\\d+)$`)) {
    const matches = index
      .map((i) => i.match(`^${ver}/(\\d+)$`))
      .filter((m) => m)
      .map((m) => parseInt(m[1]));
    const add = `${ver}/${matches.length ? Math.max(...matches) + 1 : 0}`;
    await sendRequest({
      url: "/def/$ver",
      method: "POST",
      body: {
        ver: add,
        currentVer: current,
      },
      headers: {
        ...header,
      },
    });
    current = add;
  }

  return await sendRequest({
    url: `/def`,
    method: "POST",
    body: { ...payload, ver: current },
    headers: {
      ...header,
    },
  });
};

export const getDef = async (ref, base /*: bool*/) => {
  const header = await roleHeader(undefined);
  if (base) {
    return sendRequest({
      url: `/def/${ref}`,
      method: "GET",
      headers: {
        ...header,
      },
    });
  } else {
    return sendRequest({
      url: `/def/${ref}/$get`,
      method: "POST",
      body: {
        layer: {
          name: "Root",
          kind: "Root",
          attrs: [{ name: "#CompID", value: { text: instance().compId } }],
        },
      },
      headers: {
        ...header,
      },
    });
  }
};

export const fetchDefLayerData = async (payload) => {
  if (!Array.isArray(payload?.layer?.attrs)) {
    throw new Error("Invalid request");
  }
  const header = await roleHeader(undefined);

  return sendRequest({
    url: `/def/$get`,
    method: "POST",
    body: {
      ...payload,
      layer: {
        ...payload.layer,
        attrs: [
          { name: "#CompID", value: { text: instance().compId } },
          ...payload.layer.attrs,
        ],
      },
    },
    headers: {
      ...header,
    },
  });
};

export const fetchDefData = async (ref, options, role) => {
  const header = await roleHeader(role);

  // console.log('aut :: header(fetchDefData) :>> ', header);
  return sendRequest({
    url: `/uidef/${ref.kind ? ref.name + ":" + ref.kind : ref.name ? ref.name : ref
      }?role=${role}`, //TODO Remove this - It's been added for the service worker; Service worker should check the header instead
    method: "GET",
    params: options,
    headers: {
      ...header,
    },
  });
};

export const fetchFormData = async (entityId, id, formId) => {
  const header = await roleHeader(undefined);
  return sendRequest({
    url: `/form/${formId}/${id}`,
    method: "GET",
    headers: {
      ...header,
    },
  });
};

const lookupRequestCache = {};

export const fetchLookupData = async (
  lookup,
  cascadeValue = undefined,
  query = undefined,
  id = undefined,
) => {
  // console.log('aut :: fetchLookupData(apis) ', lookup.name ,'Start ...');
  const header = await roleHeader(undefined);
  // console.log('aut :: fetchLookupData(apis) ', lookup.name ,'header :>> ',header);
  const cacheKey = JSON.stringify({
    lookupId: lookup.id,
    cascadeValue,
    query,
    id,
  });

  if (cacheKey in lookupRequestCache) return lookupRequestCache[cacheKey];

  const promise = new Promise((resolve, reject) => {
    // cascadeValue = typeof cascadeValue === "object" && encodeURIComponent(JSON.stringify(cascadeValue)) || cascadeValue;
    if (typeof cascadeValue === "object") {
      sendRequest({
        url: `/entity/$lookup/${lookup.id}`,
        method: "POST",
        body: {
          cascadeValue,
          query,
          // id: id && typeof id !== "number" ? Array.from(id).pop() : id,
          id: id,
        },
        headers: {
          ...header,
        },
      })
        .then((response) => {
          delete lookupRequestCache[cacheKey];
          resolve(response);
        })
        .catch((err) => {
          delete lookupRequestCache[cacheKey];
          reject(err);
        });
    } else if (
      lookup.fn?.script?.lang?.name == "ECore.Def.DefEnums.slJavaScript"
    ) {
      // Start JavaScript Lookup
      lookup.fn.script.fn
        .$call(lookup)
        .then((response) => {
          delete lookupRequestCache[cacheKey];
          resolve(response);
        })
        .catch((err) => {
          delete lookupRequestCache[cacheKey];
          reject(err);
        });
      // End JavaScript Lookup
    } else if (lookup.id) {
      sendRequest({
        url: `/entity/$lookup/${lookup.id}`,
        method: "GET",
        params: {
          cascadeValue,
          query,
          // id: id && typeof id !== "number" ? Array.from(id).pop() : id,
          id: id,
        },
        headers: {
          ...header,
        },
      })
        .then((response) => {
          delete lookupRequestCache[cacheKey];
          resolve(response);
        })
        .catch((err) => {
          delete lookupRequestCache[cacheKey];
          reject(err);
        });
    }
  });

  lookupRequestCache[cacheKey] = promise;

  return promise;
};

export const submitFormData = async (entityId, entity, id, formId, fd) => {
  let fileData;
  const header = await roleHeader(undefined);
  if (fd) {
    // delete entity.Files;
    fileData = new FormData();
    fd.forEach((item) => {
      fileData.append("UploadFiles", item);
    });
  }
  entity = JSON.parse(JSON.stringify(entity));

  const payload = {
    TypeData: entity.TypeData,
    PEntity: entity.PEntity,
    PGUID: entity.PGUID,
    PID: entity.PID,
    TypeID: entity.TypeID,
    Subject: entity.Subject,
    Files: entity.Files,
  };

  const clearProxy = (n) =>
    Array.isArray(n) && n.length !== 0
      ? n.map((k) => clearProxy(k))
      : n &&
      typeof n === "object" &&
      "__proxy__" in n &&
      delete n.__proxy__ &&
      Object.keys(n).map((k) => clearProxy(n[k]));
  clearProxy(entity);
  const update = id && id !== "$new";
  // const json = new Blob([JSON.stringify(entity)], {'type': 'application/json'});
  // fileData?.append("spec", json);
  // console.log(fileData);
  fileData?.append("spec", JSON.stringify(payload));
  return sendRequest({
    url: `/form/${formId}/${update ? id : ""}`,
    method: update ? "PATCH" : "POST",
    body: Array.isArray(entity.FileData) ? fileData : entity,
    headers: {
      "Content-Type": Array.isArray(entity.FileData)
        ? "multipart/form-data"
        : "application/json",
      ...header,
    },
  });
};

export const getCurrentState = async (formId, id) => {
  const header = await roleHeader(undefined);
  const endorseRegex =
    /:+(([0-9]+)|(\bfirst\b)|(\bnone\b)|(\bunversioned\b)|(\bpropagate\b)|(\bcurrent\b)|(\blatest\b)|(\bdraft\b)|(\ball\b))/;
  const checkedModelID = endorseRegex.test(id?.toString())
    ? id
    : `${id}:latest`;
  return sendRequest({
    url: `/form/${formId}/${checkedModelID}/$state`,
    method: "GET",
    headers: {
      ...header,
    },
  });
};

export const getCurrentStateMachine = async (formId, id) => {
  const header = await roleHeader(undefined);
  return sendRequest({
    url: `/form/${formId}/${id}/$statemachine`,
    method: "GET",
    signal: AbortSignal.timeout(5000),
    headers: {
      ...header,
    },
  });
};

export const invokeAction = async (formId, id, action, payload) => {
  const header = await roleHeader(undefined);
  return sendRequest({
    url: `/form/${formId}/${id}/$action/${action}`,
    method: "POST",
    body: payload,
    headers: {
      ...header,
    },
  });
};

export const advancedSearch = async (searchConfigId, query) => {
  const header = await roleHeader(undefined);
  return sendRequest({
    url: `/entity/$search/${searchConfigId}/$advanced`,
    method: "POST",
    body: query,
    headers: {
      ...header,
    },
  });
};

export const advancedSearchToExcelCSV = async (searchConfigId, query) => {
  const header = await roleHeader(undefined);
  return sendRequest({
    url: `/report/ExportToExcel/csv/entity/$search/${searchConfigId}/$advanced`,
    method: "POST",
    body: query,
    responseType: "arraybuffer",
    headers: {
      ...header,
    },
  });
};

export const advancedSearchToExcelXLSX = async (searchConfigId, query) => {
  const header = await roleHeader(undefined);
  // return axios.post(`v1/report/ExportToExcel/entity/$search/${searchConfigId}/$advanced`, query,{ responseType: "arraybuffer"  })
  return sendRequest({
    url: `/report/ExportToExcel/entity/$search/${searchConfigId}/$advanced`,
    method: "POST",
    body: query,
    headers: {
      // 'Accept': 'application/octet-stream'
    },
    responseType: "arraybuffer",
    headers: {
      ...header,
    },
  });
};

export const basicSearch = async (searchConfigId, query, folderId) => {
  if (folderId) query = { FolderID: folderId, ...query };
  const header = await roleHeader(undefined);
  return sendRequest({
    url: `/entity/$search/${searchConfigId}/$basic`,
    method: "POST",
    body: query,
    headers: {
      ...header,
    },
  });
};

export const submitDelete = async (entityId, entity, id, formId) => {
  const header = await roleHeader(undefined);
  return sendRequest({
    url: `/form/${formId}/${id}`,
    method: "DELETE",
    body: { ID: entity.ID },
    headers: {
      ...header,
    },
  });
};

export const validate = async (validationId, { value, name, model, prop }) => {
  const header = await roleHeader(undefined);
  return sendRequest({
    url: `/entity/$validation/${validationId}/$validate`,
    method: "POST",
    body: { value, name, model, prop },
    headers: {
      ...header,
    },
  });
};

export const query = async (entity, args) => {
  const header = await roleHeader(undefined);
  return sendRequest({
    url: `/entity/${entity}/$query`,
    method: "POST",
    body: args,
    headers: {
      ...header,
    },
  });
};

export const queryFn = async (fnId, args) => {
  const header = await roleHeader(undefined);
  return sendRequest({
    url: `/query/${fnId}`,
    method: "POST",
    body: args,
    headers: {
      ...header,
    },
  });
};

export const invokeFn = async (fnId, args) => {
  const header = await roleHeader(undefined);
  return sendRequest({
    url: `/entity/$fn/${fnId}/$invoke`,
    method: "POST",
    body: args,
    headers: {
      ...header,
    },
  });
};

export const getProductListBySubdomains = async () => {
  const comp = await fetchDefData("Comp", {
    exclude: ".*:(TTier|TDomain|TProductSpec|TProduct|TForm)\\.@",
    include: [
      "Comp:TTier\\.@domains",
      ".*:TDomain\\.@(subDomains|productSpec)",
      ".*:TProductSpec\\.@products",
      ".*:TProduct\\.@(label|.*Form)",
    ],
    resources: false,
    routes: false,
  });
  const subDomains = comp.domains.flatMap((d) => d.subDomains || []);
  const laProducts =
    subDomains.find((sd) => sd.name === "LA")?.productSpec?.products || [];
  const groupLAProducts =
    subDomains.find((sd) => sd.name === "GroupLA")?.productSpec?.products || [];
  return { la: laProducts, groupLA: groupLAProducts };
};

export const getProductList = async () => {
  const comp = await fetchDefData("Comp", {
    exclude: ".*:(TTier|TDomain|TProductSpec|TProduct|TForm)\\.@",
    include: [
      "Comp:TTier\\.@domains",
      ".*:TDomain\\.@(subDomains|productSpec)",
      ".*:TProductSpec\\.@products",
      ".*:TProduct\\.@(label|.*Form)",
    ],
    resources: false,
    routes: false,
  });
  return comp.domains
    .flatMap((d) => d.subDomains || [])
    .map((sd) => sd.productSpec || {})
    .flatMap((s) => s.products || []);
};

export const getClaimsList = async () => {
  const comp = await fetchDefData("Comp", {
    exclude: ".*:(TTier|TDomain|TProductSpec|TProduct|TClaim|TForm)\\.@",
    include: [
      "Comp:TTier\\.@domains",
      "*:TDomain\\.@(subDomains|productSpec)",
      "*:TProductSpec\\.@claims",
      "*:TClaim.@(label|.*Form|.*dfnID)",
    ],
    resources: false,
    routes: false,
  });
  return comp.domains
    .flatMap((d) => d.subDomains || [])
    .map((sd) => sd.productSpec || {})
    .flatMap((s) => s.claims || []);
};

// const getProduct = async (id) => {
//   if(!id) return;
//   const store = (await import("@/store")).default;
//   const key = id.name || id;
//
//   if (key in store.state.products)
//     return await store.state.products[key]
//
//   store.state.products[key] = await fetchDefData(id, {exclude: ['.*:TForm\\.@'], resources: false, routes: false})
//
//   return await store.state.products[key]
// }

export const getSetting = async (ref) => {
  const def = await fetchDefData(ref, {
    exclude:
      ".*:(Root|TTier|TDomain|TProductSpec|TProduct|TRiskCode|TBenefit|TSetting|TSettingSection|TParam)\\.@",
    include: [
      ".*\\.@(tiers|domains|subDomains|productSpec|products|allRiskCodes|allBenefits|setting|sections|params|riskCodes|benefits)",
      ".*:TParam\\\\.@(value|nestedParams)",
    ],
  });

  return compact(def);

  function compact(def) {
    const isDef = (value) =>
      typeof value === "object" && "id" in value && "name" in value;

    if (!isDef(def)) return def;

    return Object.keys(def).reduce((o, k) => {
      if (Array.isArray(def[k])) {
        if (def[k].some((v) => !isDef(v))) {
          o[k] = def[k].filter((v) => !isDef(v));
        }
        o = def[k]
          .filter((v) => isDef(v))
          .reduce((o, d) => {
            o[d.name] = compact(d);
            return o;
          }, o);
      } else if (isDef(def[k])) {
        o[def[k].name] = compact(def[k]);
      } else {
        o[k] = def[k];
      }

      return o;
    }, {});
  }
};

export const getReport = async (formId, templateName, id, queryParam) => {
  //http://insider.karestanjavan.ir/v1/report/pdf/Comp.Products.LA.ProductSpec.FULProduct.frmULPolicy-main/7987?product=Comp.Products.LA.ProductSpec.FULProduct&productTitle=%D8%B9%D9%85%D8%B1%20%D9%88%20%D9%85%D8%B3%D8%AA%D9%85%D8%B1%DB%8C
  const store = (await import("@/store")).default;
  const access_token = getAccessToken();
  const token = `Bearer ${access_token}`;
  const response = axios({
    method: "get",
    url: `/v1/pdf/${formId}-${templateName}/${id}/${queryParam}`,
    responseType: "blob",
    headers: {
      Authorization: token,
    },
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      return err;
    });
  return response;
};

export const getReportForQrCode = async (Id, requests) => {
  const store = (await import("@/store")).default;
  const access_token = getAccessToken();
  const token = `Bearer ${access_token}`;

  const responses = [];

  for (const request of requests) {
    const {
      FormId,
      ProductDefID,
      ProductTitle,
      BaseDate,
      AnnuityType,
      AnnuityTerm,
      AnnualAnnuityIndex,
      AnnuityPayMethodID,
      GuaranteedPeriod,
      ReportType,
    } = request;

    const body = {
      FormId,
      Id,
      ProductId: ProductDefID,
      ProductTitle,
      BaseDate,
      AnnuityType,
      AnnuityTerm,
      AnnualAnnuityIndex,
      AnnuityPayMethodID,
      GuaranteedPeriod,
      ReportType,
      printMark: "FinalPrint",
    };

    try {
      const response = await axios.post(
        `/v1/proposaltoprint/frmLIRPPolicy-main`,
        body,
        {
          responseType: "blob",
          headers: {
            Authorization: token,
            Accept: "application/json, text/plain, */*",
            "Accept-Language": "en-US,en;q=0.9",
          },
        },
      );

      if (response.status === 200) {
        responses.push({
          FormId,
          blob: new Blob([response.data], { type: "application/pdf" }),
        });
      } else {
        console.error(`Error response for FormId ${FormId}:`, response);
      }
    } catch (error) {
      console.error(`Error processing FormId: ${FormId}`, error);
    }
  }

  return responses;
};
export const getReportForPrintAllDocs = async (Id, requests) => {
  const store = (await import("@/store")).default;
  const access_token = getAccessToken();
  const token = `Bearer ${access_token}`;

  const responses = [];

  for (const request of requests) {
    const {
      FormId,
      ProductDefID,
      ProductTitle,
      BaseDate,
      AnnuityType,
      AnnuityTerm,
      AnnualAnnuityIndex,
      AnnuityPayMethodID,
      GuaranteedPeriod,
      ReportType,
    } = request;

    const body = {
      FormId,
      Id,
      ProductId: ProductDefID,
      ProductTitle,
      BaseDate,
      AnnuityType,
      AnnuityTerm,
      AnnualAnnuityIndex,
      AnnuityPayMethodID,
      GuaranteedPeriod,
      ReportType,
      printMark: "FinalPrint",
    };

    try {
      const response = await axios.post(
        `/v1/proposaltoprint/getMultipleDocsAbrProxy`,
        body,
        {
          responseType: "blob",
          headers: {
            Authorization: token,
            Accept: "application/json, text/plain, */*",
            "Accept-Language": "en-US,en;q=0.9",
          },
        },
      );

      if (response.status === 200) {
        responses.push({
          FormId,
          blob: new Blob([response.data], { type: "application/pdf" }),
        });
      } else {
        console.error(`Error response for FormId ${FormId}:`, response);
      }
    } catch (error) {
      console.error(`Error processing FormId: ${FormId}`, error);
    }
  }

  return responses;
};
export const getReportForGetDef = async (body) => {
  // const store = (await import("@/store")).default;
  // const access_token = getAccessToken();
  // const token = `Bearer ${access_token}`;

  const response = await axios.post(`/v1/proposaltoprint/getData`, body, {
    responseType: "blob",
    headers: {
      // Authorization: token,
      Accept: "application/json, text/plain, */*",
      "Accept-Language": "en-US,en;q=0.9",
    },
  });

  return response;
};

export const getReportForPrint = async (Id, request) => {
  const accessToken = getAccessToken();

  const body = {
    Id,
    FormId: request.FormId,
    ProductDefId: request.ProductDefID,
    ProductTitle: request.ProductTitle,
    PrintMark: request.PrintMark || "TestPrint",
    TemplateName: request.TemplateName,
  };

  const headers = {
    Accept: "application/pdf",
  };

  if (accessToken != null) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await axios.post(
    "/v1/proposaltoprint/printPdf",
    body,
    {
      responseType: "blob",
      headers,
    },
  );

  return new Blob([response.data], {
    type: "application/pdf",
  });
};


export const workflow = {
  BASE_URL: "http://192.168.1.34:8080/engine-rest",
  getFilters: async function () {
    const res = await axios.get(`${this.BASE_URL}/filter`);
    console.log(res);
    return res?.data;
  },
  getTasks: async function (id) {
    const res = await axios.get(`${this.BASE_URL}/filter/${id}/list`);
    return res?.data;
  },
  getTaskVariables: async function (id = null) {
    const res = await axios.get(`${this.BASE_URL}/task/${id}/variables`);
    console.log(res);
    return res?.data;
  },
  getTask: async function (id = null) {
    const res = await axios.get(`${this.BASE_URL}/task/${id}`);
    console.log(res);
    return res?.data;
  },
  startProcess: async function (payload) {
    console.log(payload);
    const res = await axios.post(`${this.BASE_URL}/StartProcess`, payload);
    console.log(res);
  },
};

export const getMultipleDocs = async (docs) => {
  const { data } = await axios.get("/v1/pdf/printMultipleDocs", {
    params: { ...docs },
    responseType: "blob",
    headers: { "Content-Type": "application/json" },
  });

  return data;
};

export const getDocument = (id) => {
  return sendRequest({
    url: `/document/${id}`,
    method: "GET",
  });
};

export const saveDocument = (payload) => {
  let formData = new FormData();
  formData.append("file", payload, payload.name);
  return new Promise((resolve, reject) => {
    sendRequest({
      url: `/document`,
      method: "POST",
      body: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then((response) => {
        resolve(response);
      })
      .catch(() => {
        reject(false);
      });
  });
};

export const downloadDoc = (id) => {
  return sendRequest({
    url: `/document/${id}/$download`,
    method: "GET",
  });
};

export const deleteDoc = (id) => {
  return new Promise((resolve, reject) => {
    sendRequest({
      url: `/document/${id}`,
      method: "DELETE",
    })
      .then(() => {
        resolve(true);
      })
      .catch(() => {
        reject(false);
      });
  });
};
